/**
 * The dice behind medicine (M4-01, M4-02).
 *
 * `interact.ts` owns the *verbs* — the channels, the gate, the narration — for
 * the same reason it owns loot and drag: they are all things one body does to
 * another (or to itself) at arm's length, and they share a clock and a
 * cancellation rule. This module owns the **arithmetic** those verbs roll, and
 * it is kept apart for the reason `forage.ts` is kept apart from the affordance
 * registry: the balance of a treatment — how many dice a wit buys, what a
 * poultice is worth, how much darkness costs — is a tuning surface, and a tuning
 * surface wants one file with no world state in it.
 *
 * Everything here is a pure function of numbers and one `Rng`. The AoD medicine
 * roll is the combat roll in a quieter room: a pool of d10s, a success on 6+,
 * and — the one thing a fight does not have — a **fumble**, because a slipped
 * hand in a wound is the whole drama of first aid and a missed sword swing is
 * not. That is why this does not simply call `rollSuccesses` from `exchange.ts`:
 * it needs to know not just how many dice came up, but whether one came up a 1.
 */
import { grit } from './body.js';
import { medicineDiceBonus } from './item.js';
import { litAt } from './light.js';
import { tileAt, tileDefAt } from './map.js';
import { DIRECTION_VECTORS } from './actor.js';
import { feltPain } from './vitals.js';
import { daylight } from './weather.js';
/* -------------------------------------------------------------------------- *
 * The roll
 * -------------------------------------------------------------------------- */
/** The face a die must show to count, and the one that spoils a hand. */
const MEDICINE_DIE = 10;
const SUCCESS_TARGET = 6;
const BOTCH_FACE = 1;
/** Blood units per game hour a single success presses out of a bleed. */
export const BLEED_PER_SUCCESS = 3;
/** What a fumble adds to the bleed it was meant to close. Cruel, and small. */
export const FUMBLE_BLEED = 1;
/**
 * Throw the pool. Successes on 6+, and a fumble as defined on `TreatmentRoll`.
 *
 * A pool driven to zero or below by penalties throws no dice and is a fumble by
 * definition: attempting fine work you have no business attempting is exactly
 * the situation the +1 bleed is for.
 */
export function rollTreatment(rng, dice) {
    let successes = 0;
    let botched = false;
    const throws = Math.max(0, Math.floor(dice));
    for (let i = 0; i < throws; i += 1) {
        const face = rng.roll(MEDICINE_DIE);
        if (face >= SUCCESS_TARGET)
            successes += 1;
        else if (face === BOTCH_FACE)
            botched = true;
    }
    return { successes, fumble: successes === 0 && (dice <= 0 || botched) };
}
/* -------------------------------------------------------------------------- *
 * The pool
 * -------------------------------------------------------------------------- */
/** Dice a steady-handed, unhurt treater brings from wit alone (AoD §10.1). */
export function baseTreatmentDice(attributes) {
    return Math.round(attributes.wit / 4);
}
/** What self-treatment costs — working on your own body, at a bad angle. */
export const SELF_TREATMENT_PENALTY = 2;
/** What working in the dark costs. Cancelled by any light on the tile. */
export const DARKNESS_PENALTY = 1;
/** Net pain that costs one die. Below `daylight`, treatment is night work. */
const PAIN_PER_BAND = 6;
const NIGHT_DAYLIGHT = 0.35;
/**
 * How much the treater's own suffering shakes their hands.
 *
 * Grit is subtracted first, so a tough treater fights through pain a soft one
 * cannot — the same Grit that keeps them in a fight (AoD §5.2). Only unhealed
 * wounds count: an old scar does not hurt enough to matter, and if it did the
 * body would still be carrying its `pain`.
 *
 * Willow bark counts here too (M4-03), through the same `feltPain` the combat
 * pool uses. A meddyg who has taken something for their own broken ribs has
 * steadier hands, which is a small, honest, and slightly grim piece of the
 * fiction — and it falls out of having put relief on the body rather than
 * inside one rule.
 */
export function treaterPainPenalty(combatant) {
    let pain = 0;
    for (const wound of combatant.vitals.wounds) {
        if (wound.state !== 'healed')
            pain += wound.pain;
    }
    const net = feltPain(combatant.vitals, Math.max(0, pain - grit(combatant.loadout.attributes)));
    return Math.floor(net / PAIN_PER_BAND);
}
/**
 * The whole pool for one treatment, before the dice are thrown.
 *
 * Assembled here rather than in the verb so that every treatment — staunch,
 * bandage, clean, suture — reads its skill the same way, and a balance pass has
 * one function to turn. May come out negative; `rollTreatment` treats that as
 * the guaranteed-fumble it is.
 */
export function treatmentDice(combatant, lore, mods) {
    let dice = baseTreatmentDice(combatant.loadout.attributes) + medicineDiceBonus(lore);
    dice += mods.materialMod + (mods.bonus ?? 0);
    if (mods.self)
        dice -= SELF_TREATMENT_PENALTY;
    if (mods.dark)
        dice -= DARKNESS_PENALTY;
    dice -= treaterPainPenalty(combatant);
    return dice;
}
/* -------------------------------------------------------------------------- *
 * The world's contribution
 * -------------------------------------------------------------------------- */
/**
 * Is the treater working blind?
 *
 * Dark is a fact about the *tile*, not the sky: a healer kneeling in a
 * campfire's ring works in light at midnight, and one on the open moor works in
 * the dark. So the sky's ambient light has to be low **and** no fire or brand
 * reaches the tile. `litAt` is the same reading M3-06 uses for who can see you
 * by the fire, which is the honest one — if the light is enough to be seen by,
 * it is enough to bind a wound by.
 */
export function treaterInDark(lights, time, x, y, tick) {
    return daylight(time) < NIGHT_DAYLIGHT && litAt(lights, x, y, tick) <= 0;
}
/**
 * What improvising costs: a strip torn off your own back is a die worse than a
 * proper dressing (M4-01).
 *
 * The *positive* side of this ladder used to live here as a tag check and now
 * lives in content (`HerbEffect.dice`, M4-03): what yarrow is worth is a balance
 * number about yarrow, and it belongs beside the rest of the pharmacopoeia
 * rather than in a rule. What stays is this — the baseline and the penalty —
 * because a scrap of somebody's tunic is not an item in a satchel to look up.
 */
export const IMPROVISED_MATERIAL_MOD = -1;
/** The multiplier cleaning with each grade of water puts on infection risk. */
export const WATER_CLEAN_FACTOR = {
    clean: 0.25,
    river: 0.55,
    bog: 0.9,
};
/** How much further an antiseptic wash (honey, garlic) drops the risk again. */
export const ANTISEPTIC_FACTOR = 0.5;
const NEIGHBOURHOOD = [[0, 0], ...Object.values(DIRECTION_VECTORS)];
export function waterQualityAt(map, x, y) {
    let sawWater = false;
    let sawBog = false;
    for (const [dx, dy] of NEIGHBOURHOOD) {
        const kind = tileAt(map, x + dx, y + dy);
        // The well is the whole point of the village for a wounded man — clean the
        // moment it is within reach, which is what makes it worth carrying somebody
        // home to (M4-06).
        if (kind === 'well' || kind === 'hearth')
            return 'clean';
        const material = tileDefAt(map, x + dx, y + dy).material;
        if (kind === 'ford' || material === 'water')
            sawWater = true;
        if (material === 'peat')
            sawBog = true;
    }
    if (sawWater)
        return 'river';
    if (sawBog)
        return 'bog';
    // No named source: the plain water a person carries. Honest about being
    // ordinary — as good as the river, no better.
    return 'river';
}
//# sourceMappingURL=medicine.js.map